import {
  Flex,
  Card,
  Text,
  PlaceOutlinedIcon,
  PersonOutlinedIcon,
  WatchLaterOutlinedIcon,
  type CardProps,
  mergeSx,
  EventAvailableOutlinedIcon,
  EllipsisText,
} from '@shared/components';

import { ExamEventRegistrationStatusLabel } from '../exam-event-registration-status-label';
import { ExamEventTypeLabel } from '../exam-event-type-label';

export type ExamEventCardVariants =
  | 'open-for-registration'
  | 'registration-closed'
  | 'session-full'
  | 'canceled'
  | 'temporarily-closed'
  | 'scheduled';

const EXAM_EVENT_CARD_BORDER_COLOR = {
  'open-for-registration': 'success.main',
  'session-full': 'error.main',
  'registration-closed': 'grey.500',
  canceled: 'error.main',
  'temporarily-closed': 'warning.main',
  scheduled: 'info.main',
};

export type ExamEventCardProps = Pick<CardProps, 'onClick' | 'sx'> & {
  title: string;
  location: string;
  time: string;
  variant: ExamEventCardVariants;
  type: 'in-house' | 'public';
  seat: number;
  maximumSeat: number;
  roundNumber: number;
  endAdornment?: JSX.Element;
};

export default function ExamEventCard(props: Readonly<ExamEventCardProps>) {
  const {
    title,
    location,
    time,
    variant,
    type,
    seat,
    sx,
    maximumSeat,
    endAdornment,
    onClick,
    roundNumber,
  } = props;

  const borderColor = EXAM_EVENT_CARD_BORDER_COLOR[variant] ?? 'grey.500';

  return (
    <Card
      sx={mergeSx(
        (theme) => ({
          border: '1px solid',
          borderColor: theme.palette.grey[300],
          borderRadius: theme.shape.rounded(2),
          p: theme.spacing(4),
          boxShadow: 0,
        }),
        sx,
      )}
      onClick={onClick}
    >
      <Flex sx={(theme) => ({ alignItems: 'center', gap: theme.spacing(4) })}>
        <Flex
          sx={(theme) => ({
            flex: 1,
            flexDirection: 'column',
            gap: theme.spacing(3),
            borderLeft: '4px solid',
            borderColor,
            pl: theme.spacing(3),
          })}
        >
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(2),
            })}
          >
            <EllipsisText variant='body1Semibold' sx={{ wordBreak: 'break-all' }}>
              {title}
            </EllipsisText>
            <ExamEventTypeLabel variant={type} />
          </Flex>
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(3),
            })}
          >
            <Flex
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                alignItems: 'center',
                gap: theme.spacing(1),
              })}
            >
              <EventAvailableOutlinedIcon sx={{ fontSize: '16px' }} />
              <Text variant='body2'>รอบสอบที่:</Text>
              <Text variant='body2'>{roundNumber}</Text>
            </Flex>
            <Flex
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                alignItems: 'center',
                gap: theme.spacing(1),
              })}
            >
              <WatchLaterOutlinedIcon sx={{ fontSize: '16px' }} />
              <Text variant='body2'>{time}</Text>
            </Flex>
            <Flex
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                alignItems: 'center',
                gap: theme.spacing(1),
              })}
            >
              <PlaceOutlinedIcon sx={{ fontSize: '16px' }} />
              <Text variant='body2'>{location}</Text>
            </Flex>
            <Flex
              sx={(theme) => ({
                color: theme.palette.text.secondary,
                alignItems: 'center',
                gap: theme.spacing(1),
              })}
            >
              <PersonOutlinedIcon sx={{ fontSize: '16px' }} />
              <Text variant='body2'>จำนวนที่รับสมัคร:</Text>
              <Text variant='body2'>
                {seat}/{maximumSeat}
              </Text>
            </Flex>
          </Flex>
        </Flex>
        {/* End Adornment */}
        <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
          <ExamEventRegistrationStatusLabel variant={variant} />
          {endAdornment}
        </Flex>
      </Flex>
    </Card>
  );
}
